import React, { useState, useEffect, useMemo, useCallback, useRef, useDeferredValue } from 'react';
import { ChevronRight, ChevronDown, Search, RefreshCw, Store, Loader, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';

/** Flatten nested tree into a flat array (for search/hasChildren). */
function flattenTree(nodes) {
  const result = [];
  const walk = (list) => {
    for (const n of list) {
      result.push(n);
      if (n.children && n.children.length > 0) walk(n.children);
    }
  };
  walk(nodes);
  return result;
}

const ChannelCategorySyncPage = () => {
  const { t } = useTranslation();
  const { success: notifySuccess, error: notifyError } = useNotification();

  // Store selection
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storeLoading, setStoreLoading] = useState(true);

  // Category tree
  const [treeData, setTreeData] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const syncInited = useRef(false);

  // Build id→parent_id index from tree (O(1) ancestor tracing)
  const parentMap = useMemo(() => {
    const pMap = {};
    const walk = (nodes, parentId) => {
      for (const n of nodes) {
        pMap[n.m2_category_id] = parentId;
        if (n.children) walk(n.children, n.m2_category_id);
      }
    };
    walk(treeData, null);
    return pMap;
  }, [treeData]);

  // Deferred search: keeps UI responsive during typing
  const deferredSearch = useDeferredValue(searchQuery);

  // Memoized search matching (runs on deferred value, not every keystroke)
  const searchMatchIds = useMemo(() => {
    if (!deferredSearch || !deferredSearch.trim()) return new Set();
    const q = deferredSearch.toLowerCase();
    const matching = new Set();
    // Search all flat categories
    for (const n of flatCategories) {
      if ((n.name || '').toLowerCase().includes(q)) {
        matching.add(n.m2_category_id);
      }
    }
    // Include ancestors of matched nodes (O(1·depth) via parentMap)
    const matched = [...matching];
    for (const id of matched) {
      let pid = parentMap[id];
      while (pid) {
        matching.add(pid);
        pid = parentMap[pid];
      }
    }
    return matching;
  }, [deferredSearch, flatCategories, parentMap]);

  // Error state
  const [error, setError] = useState(null);

  /** Load store list */
  useEffect(() => {
    setStoreLoading(true);
    fetch('/api/channel/stores')
      .then(r => r.json())
      .then(res => {
        if (res && res.status === 'success') {
          const list = res.data?.stores || [];
          setStores(list);
          // Auto-select first store
          if (list.length > 0 && !selectedStoreId) {
            setSelectedStoreId(String(list[0].auth_id));
          }
        }
        setStoreLoading(false);
      })
      .catch(() => setStoreLoading(false));
  }, []);

  /** Load categories for selected store */
  const loadCategories = useCallback((storeId) => {
    if (!storeId) {
      setTreeData([]);
      setFlatCategories([]);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`/api/channel/categories?store_id=${storeId}`)
      .then(r => r.json())
      .then(res => {
        if (res && res.status === 'success') {
          const cats = res.data?.tree || [];
          setTreeData(cats);
          setFlatCategories(flattenTree(cats));
        } else {
          setTreeData([]);
          setFlatCategories([]);
          setError(res?.message || 'Failed to load categories');
        }
        setLoading(false);
      })
      .catch(err => {
        setTreeData([]);
        setFlatCategories([]);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  /** Load categories when store changes */
  useEffect(() => {
    loadCategories(selectedStoreId);
  }, [selectedStoreId, loadCategories]);

  /** Handle sync button */
  const handleSync = async () => {
    if (!selectedStoreId) return;
    setSyncing(true);
    setError(null);
    try {
      const res = await fetch('/api/channel/categories/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: parseInt(selectedStoreId) }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notifySuccess(data.message || 'Sync completed');
        // Reload categories
        loadCategories(selectedStoreId);
      } else {
        setError(data.message || 'Sync failed');
        notifyError(data.message || 'Sync failed');
      }
    } catch (err) {
      setError(err.message);
      notifyError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  /** Tree rendering helpers */
  const toggleExpand = useCallback((id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const renderTree = (nodes, level = 0) => {
    if (!nodes || nodes.length === 0) return null;
    const isSearching = !!searchQuery;
    const matchMap = searchMatchIds;

    return nodes.map(c => {
      if (isSearching && !matchMap.has(c.m2_category_id)) return null;

      const hasKids = c.children && c.children.length > 0;
      const isExpanded = expanded[c.m2_category_id] || false;
      const autoExpand = isSearching && hasKids && c.children.some(ch => matchMap.has(ch.m2_category_id));
      const showChildren = isExpanded || autoExpand;

      return (
        <div key={c.m2_category_id} className="flex flex-col">
          <div
            className={`flex items-center gap-2 p-3 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors ${level === 0 ? 'font-semibold' : ''}`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
            onClick={hasKids ? () => toggleExpand(c.m2_category_id) : undefined}
          >
            <div className="w-5 shrink-0">
              {hasKids ? (
                isExpanded ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />
              ) : (
                <span className="text-slate-300 dark:text-slate-600">•</span>
              )}
            </div>
            <span className={`truncate flex-1 ${!c.is_active ? 'text-slate-400 dark:text-slate-600 line-through' : 'text-slate-700 dark:text-slate-300'}`}>
              {c.name}
            </span>
            {c.is_active ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 shrink-0">
                Active
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 shrink-0">
                Inactive
              </span>
            )}
            <span className="text-[11px] text-slate-400 shrink-0">lv.{c.level}</span>
          </div>
          {showChildren && renderTree(c.children, level + 1)}
        </div>
      );
    });
  };

  // Get selected store name
  const selectedStore = stores.find(s => String(s.auth_id) === selectedStoreId);
  const storeName = selectedStore ? (selectedStore.store_name || selectedStore.store_code || 'Store') : '—';

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <h1 className="text-2xl font-bold">类目同步</h1>
      </div>

      {/* Store selector + sync button */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              <Store size={14} className="inline mr-1" />
              渠道店铺
            </label>
            <select
              value={selectedStoreId}
              onChange={e => setSelectedStoreId(e.target.value)}
              disabled={storeLoading || syncing}
              className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {storeLoading ? (
                <option>加载中...</option>
              ) : stores.length === 0 ? (
                <option>暂无渠道店铺</option>
              ) : (
                stores.map(s => (
                  <option key={s.auth_id} value={String(s.auth_id)}>
                    {s.store_name || s.store_code || `Store #${s.auth_id}`}
                  </option>
                ))
              )}
            </select>
          </div>
          <button
            onClick={handleSync}
            disabled={!selectedStoreId || syncing || loading}
            className={`px-5 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap ${
              syncing
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            } disabled:opacity-50`}
          >
            {syncing ? (
              <>
                <Loader size={16} className="animate-spin" />
                同步中...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                同步类目
              </>
            )}
          </button>
        </div>

        {/* Status info */}
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Store size={14} />
          <span>当前店铺: <strong>{storeName}</strong></span>
          <span className="text-slate-300">|</span>
          {loading ? (
            <span className="flex items-center gap-1">
              <Loader size={12} className="animate-spin" /> 加载中...
            </span>
          ) : (
            <span>共 <strong>{flatCategories.length}</strong> 个类目</span>
          )}
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          <AlertCircle size={16} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          placeholder="搜索类目..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Category Tree */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="divide-y divide-slate-100 dark:divide-slate-700">
          {loading ? (
            <div className="p-8 flex flex-col items-center gap-3">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              <span className="text-sm text-slate-400">加载中...</span>
            </div>
          ) : !selectedStoreId ? (
            <div className="p-8 text-center text-slate-400">请选择渠道店铺</div>
          ) : treeData.length === 0 ? (
            <div className="p-8 text-center text-slate-400 flex flex-col items-center gap-2">
              <Store size={24} className="text-slate-300" />
              <span>暂无类目数据</span>
              <span className="text-xs">点击"同步类目"从 M2 拉取</span>
            </div>
          ) : (
            renderTree(treeData)
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelCategorySyncPage;
